/*
 * MediaLightbox — visor de imágenes a pantalla completa (Fase 12C).
 * Carrusel con flechas + swipe, zoom (botones, rueda, doble toque y pinch),
 * contador "12 / 64", descarga de la imagen actual y "Descargar todas" (ZIP
 * del backend). Los bytes llegan por el proxy same-origin autenticado.
 */
import { ChevronLeft, ChevronRight, Download, FolderDown, Loader2, Minus, Plus, RotateCw, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { api } from "@/lib/api";

const SCALE_MIN = 1;
const SCALE_MAX = 4;

export const MediaLightbox = ({ items, startIndex = 0, title, downloadAllHref, onClose }) => {
  const { t } = useTranslation();
  const [index, setIndex] = useState(startIndex);
  const [status, setStatus] = useState("loading"); // loading | ok | error
  const [attempt, setAttempt] = useState(0);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [downloading, setDownloading] = useState(false);
  const cache = useRef(new Map()); // media id -> object URL
  const drag = useRef(null);
  const touch = useRef(null);
  const pinch = useRef(null);

  const current = items[index];

  const fetchInto = useCallback(
    async (i) => {
      const item = items[i];
      if (!item || cache.current.has(item.id)) return true;
      const { data } = await api.get(`/media/file/${item.id}`, {
        responseType: "blob",
        params: { proxy: 1 },
        timeout: 60000,
      });
      cache.current.set(item.id, URL.createObjectURL(data));
      return true;
    },
    [items]
  );

  // carga de la imagen actual + prefetch de vecinas
  useEffect(() => {
    let alive = true;
    setScale(1);
    setOffset({ x: 0, y: 0 });
    if (cache.current.has(current?.id)) {
      setStatus("ok");
    } else {
      setStatus("loading");
      fetchInto(index)
        .then(() => alive && setStatus("ok"))
        .catch(() => alive && setStatus("error"));
    }
    fetchInto(index + 1).catch(() => {});
    fetchInto(index - 1).catch(() => {});
    return () => {
      alive = false;
    };
  }, [index, attempt, current?.id, fetchInto]);

  // limpieza de object URLs + scroll lock
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const urls = cache.current;
    return () => {
      document.body.style.overflow = prevOverflow;
      urls.forEach((u) => URL.revokeObjectURL(u));
      urls.clear();
    };
  }, []);

  const go = useCallback(
    (delta) => setIndex((i) => Math.min(items.length - 1, Math.max(0, i + delta))),
    [items.length]
  );

  // teclado
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, onClose]);

  const clampScale = (s) => Math.min(SCALE_MAX, Math.max(SCALE_MIN, s));
  const zoom = (factor) =>
    setScale((s) => {
      const next = clampScale(Number((s * factor).toFixed(2)));
      if (next === 1) setOffset({ x: 0, y: 0 });
      return next;
    });

  const onWheel = (e) => {
    e.preventDefault();
    zoom(e.deltaY < 0 ? 1.15 : 1 / 1.15);
  };

  const onDoubleClick = () =>
    setScale((s) => {
      if (s > 1) {
        setOffset({ x: 0, y: 0 });
        return 1;
      }
      return 2.5;
    });

  // pan con puntero cuando hay zoom
  const onPointerDown = (e) => {
    if (scale === 1) return;
    drag.current = { x: e.clientX - offset.x, y: e.clientY - offset.y };
  };
  const onPointerMove = (e) => {
    if (!drag.current) return;
    setOffset({ x: e.clientX - drag.current.x, y: e.clientY - drag.current.y });
  };
  const endDrag = () => {
    drag.current = null;
  };

  // swipe (1 dedo, sin zoom) + pinch (2 dedos)
  const onTouchStart = (e) => {
    if (e.touches.length === 2) {
      const [a, b] = e.touches;
      pinch.current = { dist: Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY), scale };
    } else if (e.touches.length === 1) {
      touch.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    }
  };
  const onTouchMove = (e) => {
    if (pinch.current && e.touches.length === 2) {
      const [a, b] = e.touches;
      const dist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
      setScale(clampScale(Number((pinch.current.scale * (dist / pinch.current.dist)).toFixed(2))));
    }
  };
  const onTouchEnd = (e) => {
    if (pinch.current) {
      if (e.touches.length < 2) pinch.current = null;
      return;
    }
    if (!touch.current || scale > 1) {
      touch.current = null;
      return;
    }
    const dx = e.changedTouches[0].clientX - touch.current.x;
    const dy = e.changedTouches[0].clientY - touch.current.y;
    touch.current = null;
    if (Math.abs(dx) > 56 && Math.abs(dx) > Math.abs(dy) * 1.5) go(dx < 0 ? 1 : -1);
  };

  const downloadCurrent = async () => {
    setDownloading(true);
    try {
      const { data } = await api.get(`/media/file/${current.id}`, {
        responseType: "blob",
        params: { proxy: 1, download: 1 },
        timeout: 120000,
      });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = current.original_filename || "image";
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch {
      toast.error(t("vet.downloadFailed"));
    } finally {
      setDownloading(false);
    }
  };

  const iconBtn =
    "inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors duration-200 backdrop-blur-sm disabled:opacity-30";

  const url = cache.current.get(current?.id);

  return createPortal(
    <div
      className="fixed inset-0 z-[200] bg-[#0A0A0A] flex flex-col"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      data-testid="media-lightbox"
    >
      {/* barra superior */}
      <div className="flex items-center gap-2 px-4 sm:px-6 py-3 sm:py-4">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-white truncate">{title}</p>
          <p className="text-xs text-white/50 tabular-nums" data-testid="lightbox-counter">
            {index + 1} / {items.length}
          </p>
        </div>
        <button type="button" onClick={downloadCurrent} disabled={downloading || status !== "ok"} className={iconBtn} aria-label={t("vet.downloadCurrent")} data-testid="lightbox-download-current" title={t("vet.downloadCurrent")}>
          {downloading ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : <Download className="w-4 h-4" aria-hidden="true" />}
        </button>
        {downloadAllHref && (
          <a href={downloadAllHref} className={`${iconBtn} sm:w-auto sm:px-4 sm:gap-2`} aria-label={t("vet.downloadAll")} data-testid="lightbox-download-all" title={t("vet.downloadAll")}>
            <FolderDown className="w-4 h-4 shrink-0" aria-hidden="true" />
            <span className="hidden sm:inline text-xs font-semibold">{t("vet.downloadAll")}</span>
          </a>
        )}
        <button type="button" onClick={onClose} className={iconBtn} aria-label={t("common.close")} data-testid="lightbox-close">
          <X className="w-4 h-4" aria-hidden="true" />
        </button>
      </div>

      {/* escenario */}
      <div
        className="relative flex-1 overflow-hidden touch-none select-none"
        onWheel={onWheel}
        onDoubleClick={onDoubleClick}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerLeave={endDrag}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {status === "loading" && (
          <div className="absolute inset-0 flex items-center justify-center" data-testid="lightbox-loading">
            <Loader2 className="w-7 h-7 text-white/50 animate-spin" aria-hidden="true" />
          </div>
        )}
        {status === "error" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 px-8 text-center" data-testid="lightbox-error">
            <p className="text-sm text-white/70 leading-relaxed max-w-sm">{t("vet.previewFailed")}</p>
            <button
              type="button"
              data-testid="lightbox-retry"
              onClick={() => setAttempt((a) => a + 1)}
              className="inline-flex items-center gap-2 h-10 px-6 rounded-full bg-white text-[#0A0A0A] text-xs font-semibold hover:bg-white/90 transition-colors duration-200"
            >
              <RotateCw className="w-3.5 h-3.5" aria-hidden="true" />
              {t("vet.retry")}
            </button>
          </div>
        )}
        {status === "ok" && url && (
          <div className="absolute inset-0 flex items-center justify-center p-2 sm:p-6">
            <img
              src={url}
              alt={current?.original_filename || ""}
              draggable={false}
              data-testid="lightbox-image"
              className="max-w-full max-h-full object-contain transition-transform duration-150 ease-out"
              style={{ transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`, cursor: scale > 1 ? "grab" : "zoom-in" }}
            />
          </div>
        )}

        {/* flechas */}
        {index > 0 && (
          <button type="button" onClick={() => go(-1)} className={`${iconBtn} absolute left-3 top-1/2 -translate-y-1/2`} aria-label={t("vet.previous")} data-testid="lightbox-prev">
            <ChevronLeft className="w-5 h-5" aria-hidden="true" />
          </button>
        )}
        {index < items.length - 1 && (
          <button type="button" onClick={() => go(1)} className={`${iconBtn} absolute right-3 top-1/2 -translate-y-1/2`} aria-label={t("vet.next")} data-testid="lightbox-next">
            <ChevronRight className="w-5 h-5" aria-hidden="true" />
          </button>
        )}
      </div>

      {/* controles de zoom */}
      <div className="flex items-center justify-center gap-3 pb-5 pt-2">
        <button type="button" onClick={() => zoom(1 / 1.4)} disabled={scale <= SCALE_MIN} className={iconBtn} aria-label={t("vet.zoomOut")} data-testid="lightbox-zoom-out">
          <Minus className="w-4 h-4" aria-hidden="true" />
        </button>
        <span className="text-xs text-white/50 tabular-nums w-12 text-center">{Math.round(scale * 100)}%</span>
        <button type="button" onClick={() => zoom(1.4)} disabled={scale >= SCALE_MAX} className={iconBtn} aria-label={t("vet.zoomIn")} data-testid="lightbox-zoom-in">
          <Plus className="w-4 h-4" aria-hidden="true" />
        </button>
      </div>
    </div>,
    document.body
  );
};
